import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { Button } from "components/DesignSystem/Button/Button";
import { AddShareholder } from "components/Entity/Shareholders/AddShareholder";
import { AddEditAddress } from "components/fileTax/AddEditAddress";
import { AddEditPersona } from "components/fileTax/AddEditPersona";
import { AddFilingDirectorModal } from "components/fileTax/AddFilingDirector";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import React from "react";
import { MultiValue, SingleValue } from "react-select";
import { useGetAutofillQuery } from "store/apis/autofill";
import { useGetAllShareholdersQuery } from "store/apis/Shareholders";
import {
  getUpdateChatQNAAnswerCacheKey,
  useUpdateChatQNAMutation,
} from "store/apis/chatQnA";
import { CHAT_QNA_FIELD_TYPE } from "constants/chatQnAFieldType";
import { AutofillFieldMetadata } from "types/Models/chatQnA";
import { BackendError } from "types/utils/error";
import { Tag } from "components/DesignSystem/Tag/Tag";

type Props = {
  uuid: string;
  requestId: string;
  groupId: string;
  fieldMetadata: AutofillFieldMetadata;
  currentAnswer: string | null;
  isDisabled: boolean;
  onSave?: () => void;
};

const ADD_NEW_TEXT: Record<string, string> = {
  [CHAT_QNA_FIELD_TYPE.SELECT_AUTOFILL]: "Add new",
  [CHAT_QNA_FIELD_TYPE.SELECT_SHAREHOLDER_AUTOFILL]: "Add new shareholder",
  [CHAT_QNA_FIELD_TYPE.SELECT_AUTHORIZED_SIGNATORY_AUTOFILL]: "Add new officer",
};

export const AutofillAnswerCombobox = ({
  uuid,
  requestId,
  groupId,
  fieldMetadata,
  currentAnswer,
  isDisabled,
  onSave,
}: Props) => {
  const { alertToast } = useToast();
  const [updateAnswer] = useUpdateChatQNAMutation({
    fixedCacheKey: getUpdateChatQNAAnswerCacheKey(requestId),
  });
  const {
    isOpen: showAddModal,
    open: openAddModal,
    close: closeAddModal,
  } = useModal();

  const {
    type: fieldType,
    entity_id: entityId,
    autofill_key: autofillKey,
  } = fieldMetadata;
  const isShareholderType =
    fieldType === CHAT_QNA_FIELD_TYPE.SELECT_SHAREHOLDER_AUTOFILL;

  // Authorized signatory uses "OFFICER" regardless of what autofill_key the field has.
  // This mirrors how formControl.jsx/FilingAuthorizedSignatory handles this type.
  const resolvedAutofillKey =
    fieldType === CHAT_QNA_FIELD_TYPE.SELECT_AUTHORIZED_SIGNATORY_AUTOFILL
      ? "OFFICER"
      : autofillKey;

  const { data: autofills = [], isLoading: isAutofillLoading } =
    useGetAutofillQuery(
      { groupId, autofillKey: resolvedAutofillKey, entityId },
      { skip: !groupId || !entityId || isShareholderType }
    );

  const { data: shareholders = [], isLoading: isShareholderLoading } =
    useGetAllShareholdersQuery(
      { groupId, entityId },
      { skip: !groupId || !entityId || !isShareholderType }
    );

  const isLoading = isAutofillLoading || isShareholderLoading;

  const isOfficerType =
    fieldType === CHAT_QNA_FIELD_TYPE.SELECT_AUTHORIZED_SIGNATORY_AUTOFILL;

  const options = isShareholderType
    ? shareholders.map((s) => ({
        label: (
          <div className="t-flex t-gap-1 t-items-center">
            <span>{s.name}</span>
            <Tag tagType="gray" rounded icon={false}>
              {s.ownership_stake}%
            </Tag>
          </div>
        ),
        value: s.uuid,
      }))
    : autofills.map((a) => {
        const label = isOfficerType
          ? `${a.persona_autofill?.first_name ?? ""} ${
              a.persona_autofill?.last_name ?? ""
            }`.trim()
          : a.autofill_string ?? String(a.uuid);
        return { label, value: String(a.uuid) };
      });

  const selectedUuids = currentAnswer ? currentAnswer.split(",") : [];
  const selected = isShareholderType
    ? options
        .filter((o) => selectedUuids.includes(o.value))
        .map((v) => ({
          label: shareholders.find((i) => i.uuid === v.value)?.name,
          value: v.value,
        }))
    : options.find((o) => o.value === currentAnswer) ?? null;

  const saveAnswer = async (answer: string | null) => {
    try {
      await updateAnswer({
        answerRequestId: uuid,
        requestedAnswer: answer,
        requestId,
        groupId,
      }).unwrap();
      onSave?.();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Failed to save selection",
      });
    }
  };

  const handleSingleChange = (option: { value: string } | null) => {
    if (!option) {
      return void saveAnswer(null);
    }
    void saveAnswer(option.value);
  };

  const handleMultiChange = (selected: MultiValue<OptionData>) => {
    const joined = selected.map((o) => o.value).join(",");
    void saveAnswer(joined);
  };

  const handleCreated = (newEntry: { uuid: string }) => {
    closeAddModal();
    if (isShareholderType) {
      const next = [...selectedUuids, newEntry.uuid].join(",");
      void saveAnswer(next);
    } else {
      handleSingleChange({ value: String(newEntry.uuid) });
    }
  };

  const addNewText = ADD_NEW_TEXT[fieldType];

  return (
    <>
      <Combobox
        options={options}
        isMulti={isShareholderType}
        onChange={(
          option: SingleValue<OptionData> | MultiValue<OptionData>
        ) => {
          console.log(option);
          if (isShareholderType) {
            handleMultiChange(option as MultiValue<OptionData>);
          } else {
            handleSingleChange(option as SingleValue<OptionData>);
          }
        }}
        value={selected}
        isDisabled={isDisabled || isLoading}
        placeholder="Select..."
        menuPortalTarget={document.body}
        actions={
          addNewText && !isDisabled ? (
            <Button type="button" customType="link" onClick={openAddModal}>
              {addNewText}
            </Button>
          ) : undefined
        }
      />

      {fieldType === CHAT_QNA_FIELD_TYPE.SELECT_AUTOFILL &&
        autofillKey === "addresses" && (
          <AddEditAddress
            isOpenAdd={showAddModal}
            onClose={closeAddModal}
            entityId={entityId}
            groupId={groupId}
            autofillKey={autofillKey}
            onSuccess={handleCreated}
          />
        )}

      {fieldType === CHAT_QNA_FIELD_TYPE.SELECT_AUTOFILL &&
        autofillKey === "personas" && (
          <AddEditPersona
            isOpenAdd={showAddModal}
            onClose={closeAddModal}
            entityId={entityId}
            groupId={groupId}
            autofillKey={autofillKey}
            onSuccess={handleCreated}
          />
        )}

      {fieldType ===
        CHAT_QNA_FIELD_TYPE.SELECT_AUTHORIZED_SIGNATORY_AUTOFILL && (
        <AddFilingDirectorModal
          key={String(showAddModal)}
          open={showAddModal}
          onClose={closeAddModal}
          groupId={groupId}
          entityId={entityId}
          isLoading={false}
          autofillKey="OFFICER"
          title="Add New Authorized Signatory"
          onDirectorCreate={(director) =>
            handleCreated({ uuid: director.uuid })
          }
        />
      )}

      {fieldType === CHAT_QNA_FIELD_TYPE.SELECT_SHAREHOLDER_AUTOFILL && (
        <AddShareholder
          isOpen={showAddModal}
          close={closeAddModal}
          entityId={entityId}
          onSuccess={(shareholder) => handleCreated({ uuid: shareholder.uuid })}
        />
      )}
    </>
  );
};
